import React, { useEffect, useState, useMemo } from 'react';
import {
  getVerboseIssues,
  addVerboseIssue,
  updateVerboseIssue,
  deleteVerboseIssue
} from '../services/firebaseService';
import ImageUploadField from './ImageUploadField';
import {
  FaPlus,
  FaCalendarAlt,
  FaCheckCircle,
  FaExclamationCircle,
  FaTimes,
  FaSearch,
  FaBook,
  FaStar,
  FaImage
} from 'react-icons/fa';
import { formatIssueRecord, formatVolumeIssue, formatIssuePeriod } from '../utils/issueFormat';

const defaultForm = {
  Vol: '1',
  issue: '1',
  coverPage: '',
  isArchived: false,
  isCurrentIssue: false,
  isUpcoming: false,
  validity: 'January–June 2025',
  createdAt: new Date().toISOString().slice(0, 10),
  tableOfContents: '',
};

const ResearchVerboseAdmin = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    fetchIssues();
  }, []);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const data = await getVerboseIssues();
      // Sort issues by Volume and Issue descending
      const sorted = (data || []).sort((a, b) => {
        const volA = parseInt(a.Vol, 10) || 0;
        const volB = parseInt(b.Vol, 10) || 0;
        if (volB !== volA) return volB - volA;
        const issA = parseInt(a.issue, 10) || 0;
        const issB = parseInt(b.issue, 10) || 0;
        return issB - issA;
      });
      setIssues(sorted);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Failed to fetch issues from database.');
    }
    setLoading(false);
  };

  const handleOpenAdd = () => {
    const nextVol = issues.length > 0 ? (issues[0].Vol || '1') : '1';
    const nextIssue = issues.length > 0 ? ((parseInt(issues[0].issue, 10) || 1) + 1).toString() : '1';

    setForm({
      ...defaultForm,
      Vol: nextVol,
      issue: nextIssue,
      createdAt: new Date().toISOString().slice(0, 10)
    });
    setModalMode('add');
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (issue) => {
    setForm({
      Vol: issue.Vol || '1',
      issue: issue.issue || '1',
      coverPage: issue.coverPage || '',
      isArchived: issue.isArchived || false,
      isCurrentIssue: issue.isCurrentIssue || false,
      isUpcoming: issue.isUpcoming || false,
      validity: issue.validity || '',
      createdAt: issue.createdAt && typeof issue.createdAt === 'object'
        ? (issue.createdAt.seconds || issue.createdAt._seconds
          ? new Date((issue.createdAt.seconds || issue.createdAt._seconds) * 1000).toISOString().slice(0, 10)
          : '')
        : (typeof issue.createdAt === 'string' ? issue.createdAt.slice(0, 10) : ''),
      tableOfContents: issue.tableOfContents || '',
    });
    setModalMode('edit');
    setEditingId(issue.id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setForm(defaultForm);
    setEditingId(null);
  };

  const handleSaveModal = async (e) => {
    e.preventDefault();
    if (!form.Vol || !form.issue) {
      alert('Volume and Issue numbers are required.');
      return;
    }
    if (!form.validity) {
      alert('Validity / Publication period is required (e.g. January–June 2025).');
      return;
    }

    setSaving(true);
    setError('');
    try {
      // If marking as current issue, unmark other issues
      if (form.isCurrentIssue) {
        for (const other of issues) {
          if (other.id !== editingId && other.isCurrentIssue) {
            await updateVerboseIssue(other.id, { isCurrentIssue: false });
          }
        }
      }

      if (modalMode === 'edit' && editingId) {
        await updateVerboseIssue(editingId, form);
        setSuccess(`Updated Volume ${form.Vol}, Issue ${form.issue}`);
      } else {
        await addVerboseIssue(form);
        setSuccess(`Added new Volume ${form.Vol}, Issue ${form.issue}`);
      }

      handleCloseModal();
      fetchIssues();
      setTimeout(() => setSuccess(''), 3500);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to save issue.');
    } finally {
      setSaving(false);
    }
  };

  // 1-Click Set as Current Issue
  const handleSetCurrentIssue = async (targetIssue) => {
    setSaving(true);
    setError('');
    try {
      for (const iss of issues) {
        if (iss.id === targetIssue.id) {
          await updateVerboseIssue(iss.id, { isCurrentIssue: true, isArchived: false });
        } else if (iss.isCurrentIssue) {
          await updateVerboseIssue(iss.id, { isCurrentIssue: false });
        }
      }
      setSuccess(`Volume ${targetIssue.Vol}, Issue ${targetIssue.issue} is now set as the live Current Issue!`);
      fetchIssues();
      setTimeout(() => setSuccess(''), 3500);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to set active issue.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete ${title}?`)) return;
    setLoading(true);
    try {
      await deleteVerboseIssue(id);
      setSuccess('Issue deleted successfully!');
      fetchIssues();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to delete issue.');
    }
    setLoading(false);
  };

  // Filtered issues
  const filteredIssues = useMemo(() => {
    if (!searchQuery.trim()) return issues;
    const q = searchQuery.toLowerCase();
    return issues.filter((i) =>
      (i.Vol && i.Vol.toString().toLowerCase().includes(q)) ||
      (i.issue && i.issue.toString().toLowerCase().includes(q)) ||
      (i.validity && i.validity.toLowerCase().includes(q))
    );
  }, [issues, searchQuery]);

  return (
    <div className="space-y-6">
      {/* Top Banner & Control Bar */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-serif font-bold text-[#0B3B60]">
              Journal Issues & Volumes
            </h2>
            <span className="text-xs bg-blue-100 text-[#03478E] font-bold px-2.5 py-0.5 rounded-full">
              {issues.length} Issues
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Manage bi-annual publication volumes, issue periods, cover artwork, and declare the active current issue.
          </p>
        </div>

        <button
          type="button"
          onClick={handleOpenAdd}
          className="inline-flex items-center gap-2 bg-[#03478E] hover:bg-[#023366] text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-sm transition flex-shrink-0 self-start md:self-auto"
        >
          <FaPlus className="text-sm" />
          <span>Add New Issue</span>
        </button>
      </div>

      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs flex items-center gap-2">
          <FaExclamationCircle className="text-base flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-xs flex items-center gap-2">
          <FaCheckCircle className="text-base flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Filter Toolbar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
          <span>Sort: Newest Volume & Issue First</span>
        </div>

        <div className="relative w-full sm:w-64">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search volume, issue, year..."
            className="w-full text-xs pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30 focus:bg-white"
          />
          <FaSearch className="absolute left-2.5 top-2.5 text-gray-400 text-xs" />
        </div>
      </div>

      {/* Issues List Grid */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-400 font-medium">
          Loading issues...
        </div>
      ) : filteredIssues.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-500">
          <p className="text-sm font-semibold">No journal issues found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredIssues.map((issue) => {
            const isCurrent = issue.isCurrentIssue;

            return (
              <div
                key={issue.id}
                className={`bg-white rounded-2xl p-5 shadow-xs border transition-all ${
                  isCurrent
                    ? 'border-emerald-300 bg-emerald-50/20 ring-1 ring-emerald-300'
                    : 'border-gray-200 hover:border-blue-200 hover:shadow-sm'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Cover Preview Thumbnail */}
                  <div className="w-16 h-20 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0 flex items-center justify-center shadow-2xs">
                    {issue.coverPage ? (
                      <img
                        src={issue.coverPage}
                        alt={`Vol ${issue.Vol} Iss ${issue.issue}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/assets/img/default-cover.png';
                        }}
                      />
                    ) : (
                      <FaBook className="text-gray-400 text-2xl" />
                    )}
                  </div>

                  {/* Issue Info */}
                  <div className="space-y-1.5 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-serif font-bold text-gray-900 leading-snug">
                        {formatIssueRecord(issue)}
                      </h3>
                      {isCurrent && (
                        <span className="text-[10px] font-extrabold uppercase tracking-wider bg-emerald-600 text-white px-2.5 py-0.5 rounded-full flex items-center gap-1 shadow-2xs">
                          <FaStar className="text-[9px]" />
                          <span>Current Issue</span>
                        </span>
                      )}
                      {issue.isArchived && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-500 text-white px-2 py-0.5 rounded-full">
                          Archived
                        </span>
                      )}
                      {issue.isUpcoming && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-purple-600 text-white px-2 py-0.5 rounded-full">
                          Upcoming
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-gray-600 font-semibold flex items-center gap-1.5">
                      <FaCalendarAlt className="text-gray-400 text-[11px]" />
                      <span>{issue.validity || 'Period not specified'}</span>
                    </p>

                    {issue.createdAt && (
                      <p className="text-[11px] text-gray-400">
                        Published: {typeof issue.createdAt === 'object'
                          ? (issue.createdAt.seconds || issue.createdAt._seconds
                            ? new Date((issue.createdAt.seconds || issue.createdAt._seconds) * 1000).toISOString().slice(0, 10)
                            : '')
                          : String(issue.createdAt || '').slice(0, 10)}
                      </p>
                    )}

                    {/* Bottom Actions */}
                    <div className="flex items-center gap-2 pt-2 flex-wrap">
                      {!isCurrent && (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => handleSetCurrentIssue(issue)}
                          className="text-[11px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1 rounded-lg transition"
                        >
                          ★ Set as Current Issue
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => handleOpenEdit(issue)}
                        className="text-[11px] font-bold text-[#03478E] bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-lg transition"
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(issue.id, `Volume ${issue.Vol}, Issue ${issue.issue}`)}
                        className="text-[11px] font-bold text-red-600 hover:bg-red-50 px-2.5 py-1 rounded-lg transition"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ======================= ADD / EDIT ISSUE MODAL ======================= */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-xl w-full shadow-2xl overflow-hidden border border-gray-100 my-8">
            <div className="bg-[#03478E] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-serif font-bold text-lg">
                  {modalMode === 'edit' ? `Edit Volume ${form.Vol}, Issue ${form.issue}` : 'Add New Issue Volume'}
                </h3>
                <p className="text-xs text-blue-100">
                  {modalMode === 'edit' ? 'Update validity period, cover URL, and status' : 'Register a new publication issue'}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 text-sm"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSaveModal} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              
              {/* Cover Live Preview */}
              <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <div className="w-16 h-20 rounded-xl overflow-hidden bg-white border border-gray-200 flex-shrink-0 flex items-center justify-center shadow-2xs">
                  {form.coverPage ? (
                    <img
                      src={form.coverPage}
                      alt="Cover Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/assets/img/default-cover.png';
                      }}
                    />
                  ) : (
                    <FaImage className="text-gray-400 text-xl" />
                  )}
                </div>
                <div className="text-xs min-w-0 flex-1">
                  <div className="font-serif font-bold text-gray-900 text-sm">
                    {formatVolumeIssue(form.Vol, form.issue)}
                  </div>
                  <div className="text-[#03478E] font-semibold mt-0.5">
                    {formatIssuePeriod(form.validity) || 'e.g. January–June 2026'}
                  </div>
                  <div className="text-gray-400 text-[11px] mt-1">
                    {form.isCurrentIssue ? '★ Live Current Issue' : form.isArchived ? 'Archived Issue' : 'Standard Issue'}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Volume */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Volume Number *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.Vol}
                    onChange={(e) => setForm({ ...form, Vol: e.target.value })}
                    placeholder="e.g. 1"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Issue */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Issue Number *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.issue}
                    onChange={(e) => setForm({ ...form, issue: e.target.value })}
                    placeholder="e.g. 1"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Validity Period */}
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Publication Period / Validity *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.validity}
                    onChange={(e) => setForm({ ...form, validity: e.target.value })}
                    placeholder="e.g. January–June 2025 or July–December 2025"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Cover Page Image: URL or upload */}
                <div className="md:col-span-2">
                  <ImageUploadField
                    label="Cover Page Image"
                    value={form.coverPage}
                    onChange={(url) => setForm((prev) => ({ ...prev, coverPage: url }))}
                    placeholder="https://.../cover.jpg or upload"
                  />
                </div>

                {/* Publication Date */}
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Created / Released Date
                  </label>
                  <input
                    type="date"
                    value={form.createdAt}
                    onChange={(e) => setForm({ ...form, createdAt: e.target.value })}
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Status Toggles */}
                <div className="md:col-span-2 bg-gray-50 p-4 rounded-xl border border-gray-100 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.isCurrentIssue}
                      onChange={(e) => setForm({ ...form, isCurrentIssue: e.target.checked })}
                      className="rounded text-[#03478E] focus:ring-[#03478E] w-4 h-4"
                    />
                    <span>Set as Live Current Active Issue</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.isArchived}
                      onChange={(e) => setForm({ ...form, isArchived: e.target.checked })}
                      className="rounded text-[#03478E] focus:ring-[#03478E] w-4 h-4"
                    />
                    <span>Include in Past Archives</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.isUpcoming}
                      onChange={(e) => setForm({ ...form, isUpcoming: e.target.checked })}
                      className="rounded text-[#03478E] focus:ring-[#03478E] w-4 h-4"
                    />
                    <span>Upcoming Publication</span>
                  </label>
                </div>

              </div>

              {/* Modal Actions */}
              <div className="border-t border-gray-100 pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={saving}
                  className="px-5 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-bold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#03478E] hover:bg-[#023366] text-white rounded-xl text-xs font-bold shadow-md transition disabled:opacity-50"
                >
                  {saving ? 'Saving...' : modalMode === 'edit' ? 'Save Changes' : 'Create Issue'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ResearchVerboseAdmin;